/**
 * PlanExpiredModal — informational notice shown once per login when the
 * organization's subscription has expired.
 *
 * Deliberately not a gate. The person seeing this usually cannot pay the
 * invoice themselves, and everything they could do a minute ago still works,
 * so blocking the app would punish the wrong user for a billing state.
 *
 * Self-contained rather than wrapping the shared Modal: this is a centred
 * announcement with no header bar, and shared Modal always renders a title row
 * with a corner close button. The overlay, surface and animation still come
 * from the existing .modal-overlay / .modal classes so it sits in the same
 * visual family, and Escape + backdrop dismissal are reimplemented here to
 * match what the shared component would have given.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { formatDate } from './shared';
import { useLanguage } from '../i18n/LanguageContext';

export default function PlanExpiredModal({ expiryDateUtc, onClose }) {
  const { t } = useLanguage();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-expired-title"
    >
      <div className="modal plan-expired" onClick={(e) => e.stopPropagation()}>
        <div className="plan-expired-body">
          {/* Halo ring behind a solid disc — the ring carries the alarm at a
              glance without making a 64px block of flat red the loudest thing
              on screen. */}
          <div className="plan-expired-icon" aria-hidden="true">
            <span className="plan-expired-icon-ring" />
            <span className="plan-expired-icon-disc">
              <AlertTriangle size={28} strokeWidth={2.5} />
            </span>
          </div>

          <h2 className="plan-expired-title" id="plan-expired-title">
            {t('subscriptionExpired')}
          </h2>

          <p className="plan-expired-message">{t('planExpiredMessage')}</p>

          {expiryDateUtc && (
            <div className="plan-expired-date">
              {t('expires')}: {formatDate(expiryDateUtc)}
            </div>
          )}

          <button className="plan-expired-action" onClick={onClose}>
            <X size={15} />
            {t('close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
